import { useGenerateWithAIForm } from "@/content/ai/GenerateWithAIForm"
import GenerateWithAIModal from "@/content/ai/GenerateWithAIModal"
import { EXAMPLE_POST_PLACEHOLDER } from "@/content/ai/utils/AIGenerationUtils"
import CreatePostModal from "@/post/add/CreatePostModal"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AIButton from "@components/ai/AIButton"
import { DiscoTooltip } from "@disco-ui"
import { OverridableDiscoButtonChildren } from "@disco-ui/button/OverridableDiscoButton"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { useState } from "react"

interface Props extends TestIDProps {
  feedId: GlobalID
  refetch?: VoidFunction
  children?: OverridableDiscoButtonChildren
}

function WritePostWithAIButton({
  feedId,
  refetch,
  children,
  testid = "WritePostWithAIButton",
}: Props) {
  const [showAIModal, setShowAIModal] = useState(false)
  const [showPostModal, setShowPostModal] = useState(false)
  const classes = useStyles()

  const { form } = useGenerateWithAIForm()

  const handleButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShowAIModal(true)
  }

  const handleAIFormSubmit = () => {
    if (!form.state.prompt?.trim()) {
      return
    }

    setShowAIModal(false)
    setTimeout(() => {
      setShowPostModal(true)
    }, 100)
  }

  const handleCloseAIModal = () => {
    setShowAIModal(false)
  }

  const handleClosePostModal = () => {
    setShowPostModal(false)
  }

  return (
    <>
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{ display: "inline-block" }}
        role={"button"}
        tabIndex={0}
      >
        <DiscoTooltip content={"Write with AI"}>
          <AIButton
            variant={"outlined"}
            transparentBg={true}
            testid={testid}
            onClick={handleButtonClick}
            size={"small"}
            stopPropagation={true}
            className={classes.noBorderAIButton}
          >
            {children}
          </AIButton>
        </DiscoTooltip>
      </div>

      <GenerateWithAIModal
        testid={`${testid}.AIFormModal`}
        modalContentLabel={"Post Generation"}
        title={"Describe your post"}
        isOpen={showAIModal}
        onClose={handleCloseAIModal}
        form={form}
        onSubmit={handleAIFormSubmit}
        placeholder={EXAMPLE_POST_PLACEHOLDER}
      />

      {showPostModal && (
        <CreatePostModal
          isOpen={showPostModal}
          onClose={handleClosePostModal}
          feedId={feedId}
          refetch={refetch}
          aiTemplate={"default"}
          prompt={form.state.prompt || undefined}
          referenceUrls={form.state.referenceUrls || []}
          assetEmbeddingSourceIds={form.state.assetEmbeddingSourceIds || []}
          contentEmbeddingSourceIds={form.state.contentEmbeddingSourceIds || []}
          trainingDataEmbeddingSourceIds={form.state.trainingDataEmbeddingSourceIds || []}
          searchWeb={form.state.searchWeb || false}
          communityContext={form.state.communityContext || false}
          onSubmitPost={handleSubmitPost}
        />
      )}
    </>
  )

  function handleSubmitPost() {
    // Only reset the form once the post has been submitted so they don't lose their prompt
    form.reset()
  }
}

const useStyles = makeUseStyles(() => ({
  noBorderAIButton: {
    width: "40px",
    height: "40px",
  },
}))

export default observer(WritePostWithAIButton)
